import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { CreatePropertyDto } from '../../../handling_data/dtos/PropertyDto';
import { StepRef } from './BasicInfoStep';

interface PricingStepProps {
    t: any;
    formData: CreatePropertyDto;
}

const PricingStep = forwardRef<StepRef, PricingStepProps>(({ t, formData }, ref) => {
    const [localData, setLocalData] = useState({
        price_daily: formData.price_daily,
        price_monthly: formData.price_monthly,
        price_yearly: formData.price_yearly
    });

    // Sync local state when formData changes (essential for edit flow)
    useEffect(() => {
        setLocalData({
            price_daily: formData.price_daily,
            price_monthly: formData.price_monthly,
            price_yearly: formData.price_yearly
        });
    }, [formData.price_daily, formData.price_monthly, formData.price_yearly]);

    useImperativeHandle(ref, () => ({
        getData: () => localData
    }));

    const togglePrice = (key: 'daily' | 'monthly' | 'yearly') => {
        const priceKey = `price_${key}` as 'price_daily' | 'price_monthly' | 'price_yearly';
        setLocalData(prev => {
            const currentVal = prev[priceKey];
            return {
                ...prev,
                [priceKey]: currentVal === undefined ? 0 : undefined
            };
        });
    };

    const updatePrice = (key: 'daily' | 'monthly' | 'yearly', value: number) => {
        const priceKey = `price_${key}` as 'price_daily' | 'price_monthly' | 'price_yearly';
        setLocalData(prev => ({ ...prev, [priceKey]: value }));
    };

    return (
        <div className="space-y-4 animate-fade-in">
            <h3 className="text-lg font-bold text-gray-800 border-b pb-2 mb-4">{t('property_form.pricing')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {(['daily', 'monthly', 'yearly'] as const).map(period => {
                    const priceKey = `price_${period}` as 'price_daily' | 'price_monthly' | 'price_yearly';
                    const isActive = localData[priceKey] !== undefined;
                    return (
                        <div key={period} className={`p-4 rounded-xl border transition-all ${isActive ? 'border-primary/50 bg-primary/5' : 'border-gray-200 bg-gray-50'}`}>
                            <div className="flex items-center justify-between mb-3">
                                <label className="text-sm font-bold text-gray-700">{t(`property_form.price_${period}`)}</label>
                                <div
                                    dir="ltr"
                                    onClick={() => togglePrice(period)}
                                    className={`w-10 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors ${isActive ? 'bg-primary' : 'bg-gray-300'}`}
                                >
                                    <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${isActive ? 'translate-x-4' : 'translate-x-0'}`} />
                                </div>
                            </div>
                            <div className="relative">
                                <span className={`absolute left-3 top-2 transition-colors ${isActive ? 'text-gray-500' : 'text-gray-300'}`}>$</span>
                                <input
                                    type="number"
                                    disabled={!isActive}
                                    value={isActive ? (localData[priceKey] as number) : ''}
                                    onChange={(e) => updatePrice(period, Number(e.target.value))}
                                    className={`w-full pl-8 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-primary/20 dir-ltr transition-all ${isActive ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-100 text-gray-400'}`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

export default PricingStep;
